import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FileUp, Loader2, Trash2, FileText, Image as ImageIcon, File, Download, Eye, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import api from '../lib/api';
import { shrinkImage } from '../lib/shrinkImage';

const MAX_SIZE = 2 * 1024 * 1024; // 2 MB
const ACCEPTED = '.jpg,.jpeg,.png,.webp,.gif,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx';

const ICON_MAP = {
  'application/pdf': FileText,
  'text/plain': FileText,
  'text/csv': FileText,
  default: File,
};

const fmtSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageType = (ct) => (ct || '').startsWith('image/');

/**
 * Universal file upload component — supports images and documents.
 *
 * Props:
 *  - files: array of file objects [{file_id, original_filename, content_type, size, is_image}]
 *  - onChange: (newFiles) => void
 *  - category: file category (receipt, general, etc.)
 *  - entityType: optional entity type to link
 *  - entityId: optional entity id to link
 *  - max: maximum number of files allowed
 *  - disabled: disable upload
 *  - testId: data-testid prefix
 *  - label: label text
 *  - portalClient: destination-scoped portal client; switches to patient document endpoints
 */
export const FileUploadField = ({
  files = [],
  onChange,
  category = 'general',
  entityType,
  entityId,
  max = 5,
  disabled = false,
  testId = 'file-upload',
  label = 'Attachments',
  portalClient,
  deleteOnRemove = false,
  camera = true,
  renderExtra,
  extraFields,
  description: descProp,
}) => {
  const http = portalClient || api;
  const paths = useMemo(() => (portalClient
    ? { upload: '/documents', file: (id) => `/documents/${id}` }
    : { upload: '/storage/upload', file: (id) => `/storage/files/${id}` }), [portalClient]);
  const freshUploads = useRef(new Set());
  const pendingIds = files.filter((f) => typeof f === 'string').join(',');

  useEffect(() => {
    if (!pendingIds) return;
    let live = true;
    Promise.all(files.map((f) => (typeof f !== 'string' ? f
      : http.get(paths.file(f)).then((r) => r.data)
        .catch(() => ({ file_id: f, original_filename: 'Unavailable file', size: 0, missing: true })))))
      .then((rows) => { if (live) onChange(rows); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingIds]);
  const input = useRef(null);
  const cameraInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const upload = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    setError('');
    if (files.length >= max) { setError(`Maximum ${max} files allowed.`); return; }
    setBusy(true);
    try {
      let file;
      try { file = await shrinkImage(picked, MAX_SIZE); } catch (e) { setError(e.message); return; }
      if (file.size > MAX_SIZE) { setError(`File too large. Maximum is ${fmtSize(MAX_SIZE)}.`); return; }
      const body = new FormData();
      body.append('file', file);
      body.append('category', category);
      if (entityType) body.append('entity_type', entityType);
      if (entityId) body.append('entity_id', entityId);
      if (descProp) body.append('description', descProp);
      Object.entries(extraFields || {}).forEach(([k, v]) => { if (v) body.append(k, v); });
      const res = await http.post(paths.upload, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      freshUploads.current.add(res.data.file_id);
      onChange([...files, res.data]);
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = useCallback(async (index) => {
    const file = files[index];
    const id = file?.file_id;
    if (deleteOnRemove && id) {
      if (!window.confirm(`Delete "${file.original_filename}"? This cannot be undone.`)) return;
      try {
        await http.delete(paths.file(id));
      } catch (e) {
        setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not delete the file.');
        return;
      }
    } else if (id && freshUploads.current.has(id)) {
      http.delete(paths.file(id)).catch(() => {});
    }
    freshUploads.current.delete(id);
    onChange(files.filter((_, i) => i !== index));
  }, [files, onChange, http, paths, deleteOnRemove]);

  const preview = useCallback(async (f) => {
    setPreviewFile(f);
    setPreviewLoading(true);
    try {
      const res = await http.get(`${paths.file(f.file_id)}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch {
      setError('Could not load preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [http, paths]);

  const closePreview = () => {
    setPreviewFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const downloadFile = useCallback(async (f) => {
    try {
      const res = await http.get(`${paths.file(f.file_id)}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.original_filename || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed.');
    }
  }, [http, paths]);

  const FileIcon = (ct) => {
    if (isImageType(ct)) return ImageIcon;
    return ICON_MAP[ct] || ICON_MAP.default;
  };

  return (
    <div className="relative space-y-2" data-testid={`${testId}-field`}>
      {label && <label className="text-sm font-medium">{label}</label>}

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-1.5" data-testid={`${testId}-list`}>
          {files.map((f, i) => {
            if (typeof f === 'string') return null;
            const Icon = FileIcon(f.content_type);
            return (
              <div key={f.file_id || i} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2.5 py-1.5 rounded-md border bg-slate-50 dark:bg-slate-900 text-sm group" data-testid={`${testId}-item-${i}`}>
                <Icon className="h-4 w-4 shrink-0 text-slate-500" />
                <button type="button" disabled={f.missing} onClick={() => f.is_image ? preview(f) : downloadFile(f)}
                  className="truncate flex-1 min-w-0 text-left hover:underline disabled:no-underline disabled:text-muted-foreground"
                  title={f.original_filename} data-testid={`${testId}-open-${i}`}>
                  {f.original_filename}
                </button>
                {f.origin_label && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 shrink-0" data-testid={`${testId}-origin-${i}`}>{f.origin_label}</span>}
                <span className="text-xs text-muted-foreground shrink-0">{fmtSize(f.size)}</span>
                {!f.missing && <button type="button" onClick={() => f.is_image ? preview(f) : downloadFile(f)}
                  className="md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 p-0.5 rounded hover:bg-slate-200 transition-opacity"
                  data-testid={`${testId}-preview-${i}`} title={f.is_image ? 'Preview' : 'Download'}>
                  {f.is_image ? <Eye className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
                </button>}
                {!disabled && !f.locked && (
                  <button type="button" onClick={() => remove(i)}
                    className="md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 p-0.5 rounded hover:bg-red-100 text-red-600 transition-opacity"
                    data-testid={`${testId}-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                {renderExtra && <div className="basis-full pl-6">{renderExtra(f, i)}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload button */}
      {files.length < max && !disabled && (
        <div className="flex flex-wrap items-center gap-2">
          <input ref={input} type="file" accept={ACCEPTED} disabled={busy} onChange={upload}
            className="absolute opacity-0 w-0 h-0 overflow-hidden" data-testid={`${testId}-input`} />
          {camera && (
            <>
              <input ref={cameraInput} type="file" accept="image/*" capture="environment" disabled={busy} onChange={upload}
                className="absolute opacity-0 w-0 h-0 overflow-hidden" data-testid={`${testId}-camera-input`} />
              <Button type="button" variant="outline" size="sm" disabled={busy}
                onClick={() => cameraInput.current?.click()} data-testid={`${testId}-camera-btn`}>
                <Camera className="mr-2 h-4 w-4" /> Take photo
              </Button>
            </>
          )}
          <Button type="button" variant="outline" size="sm" disabled={busy}
            onClick={() => input.current?.click()} data-testid={`${testId}-btn`}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}
            {busy ? 'Uploading…' : 'Attach file'}
          </Button>
          <span className="text-xs text-muted-foreground" data-testid={`${testId}-hint`}>
            Up to {fmtSize(MAX_SIZE)} (photos shrink automatically) · {files.length}/{max} files
          </span>
        </div>
      )}

      {error && <p role="alert" className="text-xs text-red-700" data-testid={`${testId}-error`}>{error}</p>}

      {/* Preview dialog */}
      <Dialog open={!!previewFile} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-lg" data-testid={`${testId}-preview-dialog`}>
          <DialogHeader>
            <DialogTitle className="truncate">{previewFile?.original_filename}</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : previewUrl && previewFile?.is_image ? (
            <div className="rounded-lg overflow-hidden border bg-slate-50 flex items-center justify-center" style={{ maxHeight: '60vh' }}>
              <img src={previewUrl} alt={previewFile.original_filename} className="max-h-[60vh] w-auto object-contain" data-testid={`${testId}-preview-image`} />
            </div>
          ) : previewUrl && previewFile?.content_type === 'application/pdf' ? (
            <iframe src={previewUrl} title={previewFile.original_filename} className="w-full rounded-lg border" style={{ height: '60vh' }} data-testid={`${testId}-preview-pdf`} />
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={() => previewFile && downloadFile(previewFile)} data-testid={`${testId}-preview-download`}>
              <Download className="mr-2 h-4 w-4" /> Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
